/**
 * Trading wallets, customer-signed permissions, withdrawals, orders and the portfolio (docs/API.md "Trading wallets
 * and permissions", ADR-0010). arbitrage holds the wallets and keys; algo.boo relays the customer's EIP-712
 * signatures (`AppPermission`, `PermissionAction`, `Withdrawal`, contract §4.2 and §17) and mirrors the permission
 * record in `accounts.permissions`. Amounts are wei decimal strings of the named token.
 */
import { z } from 'zod'
import { Address, BlockNumber, Hash32, HexData, IsoTimestamp, UsdString, Uuid, WeiString } from '../primitives.js'
import { PermissionCapability, PermissionStatus, TradingWalletStatus } from '../enums.js'
import { AgentSummary, TokenRef } from './common.js'

/** Signed decimal integer string (statement deltas, realized pnl). */
export const SignedWeiString = z.string().regex(/^-?(0|[1-9][0-9]*)$/, 'expected a signed decimal integer string')
export type SignedWeiString = z.output<typeof SignedWeiString>

/** A permission cap: a wei decimal string that fits the signed EIP-712 `uint256` (larger is 400, never a 500). */
export const Uint256String = WeiString.refine((s) => /^[0-9]{1,78}$/.test(s) && BigInt(s) <= 2n ** 256n - 1n, 'must fit in uint256')
export type Uint256String = z.output<typeof Uint256String>

/** A 65-byte ECDSA signature (r, s, v) as lowercase 0x hex. */
export const Signature65 = HexData.refine((s) => s.length === 132, 'expected a 65-byte signature')
export type Signature65 = z.output<typeof Signature65>

/** Decimal string of a uint64 (EIP-712 nonces). */
export const NonceString = z.string().regex(/^(0|[1-9][0-9]{0,19})$/, 'expected a uint64 decimal string')

/** arbitrage references (`prm_…`, `ord_…`, `lot_…`, `stg_…`, `wrq_…`, `wdr_…`). */
export const ArbRef = z.string().regex(/^[a-z]{3}_[0-9a-z]{16,40}$/, 'expected an arbitrage reference')

/** `clientOrderId` algo.boo gives arbitrage (the proposal id or a strategy-derived id). */
export const ClientOrderId = z.string().regex(/^[A-Za-z0-9_.:-]{1,128}$/, 'expected a client order id')

// EIP-712 ------------------------------------------------------------------------------------------------------

/**
 * EIP-712 typed data as JSON: uint fields are decimal strings. Clients convert `uint*` fields to bigint before
 * signing (wagmi `signTypedData`); the API rebuilds the same struct and verifies the signature before relaying.
 */
export const TypedDataJson = z.object({
  domain: z.object({ name: z.string(), version: z.string(), chainId: z.number().int().positive() }),
  types: z.record(z.string(), z.array(z.object({ name: z.string(), type: z.string() }))),
  primaryType: z.string(),
  message: z.record(z.string(), z.union([z.string(), z.number()])),
})
export type TypedDataJson = z.output<typeof TypedDataJson>

// Wallets ------------------------------------------------------------------------------------------------------

/** A trading wallet: arbitrage derives it for the customer asynchronously (`requested` → `ready`). */
/**
 * The account's trading wallet (decision 1 of 30 Sep, lane BOOCLEAN1): one HD-custody wallet per account, no choice
 * among several and so no name to choose it by.
 */
export const TradingWallet = z.object({
  id: Uuid,
  /** Deposit address; null until arbitrage derived it. */
  address: Address.nullable(),
  status: TradingWalletStatus,
  /** arbitrage wallet request (`wrq_…`). */
  requestRef: z.string(),
  /** Why arbitrage refused the request (`WALLET_LIMIT`, …); null otherwise. */
  refusalCode: z.string().nullable(),
  derivedAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
})
export type TradingWallet = z.output<typeof TradingWallet>

/** `GET /wallet`: the account's one trading wallet, null until it asked for one. */
export const AccountWallet = z.object({ wallet: TradingWallet.nullable() })
export type AccountWallet = z.output<typeof AccountWallet>

export const WalletBalance = z.object({
  token: TokenRef,
  /** null = unknown (ledger not readable), never zero. */
  amount: WeiString.nullable(),
  /** Committed to in-flight orders. */
  pending: WeiString,
  asOfBlock: BlockNumber.nullable(),
})
export type WalletBalance = z.output<typeof WalletBalance>

export const WalletBalances = z.object({ wallet: Address, observedAt: IsoTimestamp, balances: z.array(WalletBalance) })
export type WalletBalances = z.output<typeof WalletBalances>

/** One lot (arbitrage keeps positions per wallet, strategy and token, contract §6.3). */
export const WalletLot = z.object({
  lotRef: z.string(),
  token: TokenRef,
  quoteToken: TokenRef,
  /** The agent the lot belongs to (via its permission); null for lots arbitrage did not attribute. */
  agent: AgentSummary.nullable(),
  permissionRef: z.string().nullable(),
  strategyRef: z.string().nullable(),
  amountInitial: WeiString,
  amountRemaining: WeiString,
  /** Quote-token wei paid for the lot. */
  costBasis: WeiString,
  realizedPnl: SignedWeiString,
  dryRun: z.boolean(),
  openedAt: IsoTimestamp,
  closedAt: IsoTimestamp.nullable(),
})
export type WalletLot = z.output<typeof WalletLot>

export const WalletPositionsQuery = z.object({ includeClosed: z.enum(['true', 'false']).optional() })
export const WalletPositions = z.object({ wallet: Address, observedAt: IsoTimestamp, lots: z.array(WalletLot) })
export type WalletPositions = z.output<typeof WalletPositions>

export const StatementKind = z.enum(['deposit', 'withdrawal', 'fill_in', 'fill_out', 'fee', 'gas'])
export const WalletStatementEntry = z.object({
  kind: StatementKind,
  at: IsoTimestamp,
  token: TokenRef,
  /** Signed change of the wallet's balance of `token`. */
  amount: SignedWeiString,
  orderRef: z.string().nullable(),
  txHash: Hash32.nullable(),
  blockNumber: BlockNumber.nullable(),
})
export type WalletStatementEntry = z.output<typeof WalletStatementEntry>

/** Default: the last 30 days. */
export const WalletStatementQuery = z.object({ from: IsoTimestamp.optional(), to: IsoTimestamp.optional() })
export const WalletStatement = z.object({ wallet: Address, from: IsoTimestamp, to: IsoTimestamp, entries: z.array(WalletStatementEntry) })
export type WalletStatement = z.output<typeof WalletStatement>

// Permissions --------------------------------------------------------------------------------------------------

/**
 * The terms the customer signs as `AppPermission` (contract §4, §17.4). Caps are wei of `quoteToken`. Empty
 * `tokenAllow` = any token not in `tokenDeny`; empty `recipients` = the wallet only. `venues` are ids from
 * `GET /permissions/options` (at least one).
 */
export const PermissionTerms = z.object({
  capabilities: z.array(PermissionCapability).min(1),
  venues: z.array(z.string().min(1)).min(1),
  tokenAllow: z.array(Address).default([]),
  tokenDeny: z.array(Address).default([]),
  quoteToken: Address,
  maxPerOrder: Uint256String,
  maxPerWindow: Uint256String,
  windowSeconds: z.number().int().min(60).max(31_536_000),
  maxExposure: Uint256String,
  recipients: z.array(Address).default([]),
  expiresAt: IsoTimestamp,
})
export type PermissionTerms = z.output<typeof PermissionTerms>
export type PermissionTermsInput = z.input<typeof PermissionTerms>

/** Narrowing by the app alone: any subset of the terms, each no wider than the current one (else `PERMISSION_WIDENING`). */
export const NarrowPermission = z
  .object({
    capabilities: z.array(PermissionCapability).min(1),
    venues: z.array(z.string().min(1)).min(1),
    tokenAllow: z.array(Address),
    tokenDeny: z.array(Address),
    maxPerOrder: Uint256String,
    maxPerWindow: Uint256String,
    windowSeconds: z.number().int().min(60).max(31_536_000),
    maxExposure: Uint256String,
    expiresAt: IsoTimestamp,
  })
  .partial()
  .refine((o) => Object.keys(o).length > 0, 'at least one term')
export type NarrowPermission = z.input<typeof NarrowPermission>

/** `POST /permissions/typed-data` kind `grant` and `POST /permissions`: which agent may act on which wallet. */
export const PermissionGrant = z.object({ agentId: Uuid, wallet: Address, terms: PermissionTerms })
export type PermissionGrant = z.input<typeof PermissionGrant>

export const PermissionActionName = z.enum(['pause', 'resume', 'revoke'])
export type PermissionActionName = z.output<typeof PermissionActionName>

/**
 * Contract §17.27: `AppPermission.permissionRef` is chosen by algo.boo and signed. Kind `grant` allocates a fresh
 * reference (stored before the customer signs) and returns it in `TypedDataResult.permissionRef`; with
 * `permissionRef` it re-signs that permission of yours at the next version (the only way to widen one).
 */
export const PermissionTypedDataRequest = z.discriminatedUnion('kind', [
  PermissionGrant.extend({ kind: z.literal('grant'), permissionRef: ArbRef.optional() }),
  z.object({ kind: z.literal('action'), permissionRef: ArbRef, action: PermissionActionName }),
])
export type PermissionTypedDataRequest = z.input<typeof PermissionTypedDataRequest>

/** What the customer signs. `nonce`/`expiresAt` are set for `PermissionAction` and `Withdrawal` and must be echoed back. */
export const TypedDataResult = z.object({
  typedData: TypedDataJson,
  digest: Hash32,
  nonce: NonceString.nullable(),
  expiresAt: IsoTimestamp.nullable(),
  /**
   * The permission the typed data is about: for a grant the reference algo.boo allocated and the customer signs
   * (`AppPermission.permissionRef`, §17.27), echoed back in `POST /permissions`; null for a withdrawal.
   */
  permissionRef: ArbRef.nullable().default(null),
  /** For a grant: the `AppPermission.version` signed (1 for a new permission, current + 1 for a re-signing). */
  version: z.number().int().positive().nullable().default(null),
})
export type TypedDataResult = z.output<typeof TypedDataResult>

/** `permissionRef` is the one `POST /permissions/typed-data` returned (and the customer signed). */
export const CreatePermission = PermissionGrant.extend({ signature: Signature65, permissionRef: ArbRef })
export type CreatePermission = z.input<typeof CreatePermission>

/** A customer's `PermissionAction` signature (required for resume; optional for pause and revoke). */
export const PermissionActionBody = z.object({ signature: Signature65, nonce: NonceString, expiresAt: IsoTimestamp }).partial()
export type PermissionActionBody = z.input<typeof PermissionActionBody>

export const PermissionRefParams = z.object({ ref: ArbRef })

export const PermissionUsage = z.object({ windowUsed: WeiString, windowRemaining: WeiString, exposure: WeiString })
export type PermissionUsage = z.output<typeof PermissionUsage>

export const Permission = z.object({
  permissionRef: z.string(),
  agent: AgentSummary.nullable(),
  wallet: Address,
  version: z.number().int().positive(),
  status: PermissionStatus,
  /** Who paused it; resuming a customer pause needs the customer's signature. */
  pausedBy: z.enum(['app', 'customer']).nullable(),
  terms: PermissionTerms,
  typedDataHash: Hash32.nullable(),
  /** Rolling-window usage and exposure from arbitrage; null when arbitrage did not answer. */
  usage: PermissionUsage.nullable(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type Permission = z.output<typeof Permission>

export const PermissionsQuery = z.object({ wallet: Address.optional(), agentId: Uuid.optional() })

/** `GET /permissions/options`: what an authorization can name right now. */
export const PermissionOptions = z.object({
  /** Venue ids in arbitrage's capability order (the EIP-712 `venues` bitmap follows it). */
  venues: z.array(z.object({ id: z.string(), status: z.enum(['available', 'degraded', 'unavailable']) })),
  capabilities: z.array(z.object({ id: PermissionCapability, status: z.enum(['available', 'degraded', 'unavailable']) })),
  quoteTokens: z.array(TokenRef),
  /** Whether arbitrage's signer is armed (`ops.arming`); unarmed = every order is a dry run. */
  armed: z.boolean(),
})
export type PermissionOptions = z.output<typeof PermissionOptions>

// Withdrawals --------------------------------------------------------------------------------------------------

export const WithdrawalDraft = z.object({ wallet: Address, token: Address, amount: WeiString, to: Address })
export type WithdrawalDraft = z.input<typeof WithdrawalDraft>

export const CreateWithdrawal = WithdrawalDraft.extend({ nonce: NonceString, expiresAt: IsoTimestamp, signature: Signature65 })
export type CreateWithdrawal = z.input<typeof CreateWithdrawal>

export const WithdrawalResult = z.object({ withdrawalRef: z.string(), state: z.enum(['accepted', 'submitted', 'final', 'refused']) })
export type WithdrawalResult = z.output<typeof WithdrawalResult>

// Orders and fills ---------------------------------------------------------------------------------------------

export const OrderState = z.enum(['accepted', 'triggered', 'signed', 'submitted', 'included', 'final', 'reorged', 'reverted', 'dropped', 'expired', 'refused', 'cancelled'])
export type OrderState = z.output<typeof OrderState>

export const FillFee = z.object({
  token: TokenRef,
  amount: WeiString,
  state: z.enum(['collected', 'skipped', 'accrued', 'settling', 'settled', 'failed']),
  skipCode: z.string().nullable(),
  txHash: Hash32.nullable(),
})
export type FillFee = z.output<typeof FillFee>

export const OrderFill = z.object({
  fillIndex: z.number().int().min(0).max(255),
  tokenIn: TokenRef,
  tokenOut: TokenRef,
  amountIn: WeiString,
  /** The swap's gross output (contract §17.19). */
  amountOut: WeiString,
  /** What the wallet received: `amountOut` less an in-transaction fee taken in the output token. */
  netAmountOut: WeiString,
  dryRun: z.boolean(),
  state: z.enum(['included', 'final', 'reorged']),
  txHash: Hash32.nullable(),
  minedAt: IsoTimestamp.nullable(),
  fee: FillFee.nullable(),
})
export type OrderFill = z.output<typeof OrderFill>

export const WalletOrder = z.object({
  orderRef: z.string(),
  clientOrderId: z.string(),
  wallet: Address,
  permissionRef: z.string(),
  agent: AgentSummary.nullable(),
  kind: z.enum(['market', 'conditional']),
  side: z.enum(['buy', 'sell']),
  dryRun: z.boolean(),
  /** Why the order is a dry run (arbitrage `DryRunReason`): `REQUESTED` (a paper order) or `UNARMED` (a live order arbitrage's arming record kept off the wire); null when live or unknown. */
  dryRunReason: z.enum(['REQUESTED', 'UNARMED']).nullable().optional(),
  tokenIn: TokenRef,
  tokenOut: TokenRef,
  /** Resolved input amount; null until arbitrage sized the order (lot fractions). */
  amountIn: WeiString.nullable(),
  minOut: WeiString,
  state: OrderState,
  refusalCode: z.string().nullable(),
  txHash: Hash32.nullable(),
  fills: z.array(OrderFill),
  acceptedAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type WalletOrder = z.output<typeof WalletOrder>

export const OrdersQuery = z.object({ wallet: Address.optional(), state: OrderState.optional(), limit: z.coerce.number().int().min(1).max(200).default(50) })
export const ClientOrderParams = z.object({ clientOrderId: ClientOrderId })

// Portfolio ----------------------------------------------------------------------------------------------------

/** `GET /me/portfolio`: every wallet with balances, lots and permissions. `available: false` when arbitrage did not answer. */
export const PortfolioWallet = z.object({
  wallet: TradingWallet,
  balances: z.array(WalletBalance),
  lots: z.array(WalletLot),
  permissions: z.array(Permission),
})
export type PortfolioWallet = z.output<typeof PortfolioWallet>

/** `GET /me/portfolio`: the account's trading wallet with its balances, lots and permissions; null until it has one. */
export const Portfolio = z.object({ observedAt: IsoTimestamp, available: z.boolean(), wallet: PortfolioWallet.nullable() })
export type Portfolio = z.output<typeof Portfolio>

/** `GET /agent/permissions` row: a permission the calling agent may trade under. */
export const AgentPermission = z.object({
  permissionRef: z.string(),
  wallet: Address,
  status: PermissionStatus,
  terms: PermissionTerms,
  usage: PermissionUsage.nullable(),
})
export type AgentPermission = z.output<typeof AgentPermission>

/**
 * A suggested permission a link prefills the authorize dialog with (`/portfolio?card=`): capabilities, the USD caps and
 * token lists; the customer still edits and signs it. (Named after the creation card that first produced it.)
 */
export const CardPermission = z.object({
  capabilities: z.array(PermissionCapability),
  maxPerOrderUsd: UsdString,
  maxPerWindowUsd: UsdString,
  windowSeconds: z.number().int(),
  maxExposureUsd: UsdString,
  tokenAllow: z.array(Address),
  tokenDeny: z.array(Address),
})
export type CardPermission = z.output<typeof CardPermission>

